import React, { useState, useEffect, useCallback } from "react";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  TableContainer,
  TableRow,
  TableCell,
  TableHead,
  Table,
  TableBody,
  CircularProgress,
  Typography,
  Button,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CollectionsIcon from "@mui/icons-material/Collections";
import LoginIcon from "@mui/icons-material/Login";

import googleDriveAxios from "../../common/googleDriveAxios";
import { toast } from "react-toastify";

const ImageList: React.FC<{
  chassisDCNumber: string;
  checkPointName: string;
  deptName: string;
  imagesExist: boolean;
}> = ({ chassisDCNumber, checkPointName, deptName, imagesExist }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [imageList, setImageList] = useState<string[]>([]);
  const [apiError, setApiError] = useState<boolean>(false);
  const [isImageselected, setIsImagesSelected] = useState(false);
  const [googleDriveUrl, setGoogleDriveUrl] = useState("");

  const [, setIsAuthenticated] = useState<boolean | null>(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState<boolean>(false);

  const checkAuthentication = useCallback(async () => {
    try {
      setLoading(true);
      const response = await googleDriveAxios.get("/auth/login");
      const isAuth = response.data.message === "Already authenticated";
      setIsAuthenticated(isAuth);
      setLoading(false);
      return isAuth;
    } catch (error) {
      console.error("Authentication check failed:", error);
      setIsAuthenticated(false);
      setLoading(false);
      return false;
    }
  }, []);

  const getAllImageNames = useCallback(async () => {
    setLoading(true);
    if (checkPointName && chassisDCNumber) {
      try {
        const queryParams = new URLSearchParams({
          chassis_no: chassisDCNumber,
          folder_name: deptName + "_" + checkPointName,
        }).toString();

        const response = await googleDriveAxios.post(
          `/get-all-image-names?${queryParams}`
        );

        setImageList(response.data);
      } catch (error: any) {
        if (error.response && error.response.status === 401) {
          setIsAuthenticated(false);
          setShowLoginPrompt(true);
        } else {
          setApiError(true);
        }
      } finally {
        setLoading(false);
      }
    }
  }, [
    chassisDCNumber,
    checkPointName,
    deptName,
    setIsAuthenticated,
    setShowLoginPrompt,
  ]);

  const handleGoogleLogin = useCallback(async () => {
    try {
      setLoading(true);
      const response = await googleDriveAxios.get("/auth/login");

      if (response.data.authUrl) {
        localStorage.setItem(
          "imageListReturnPath",
          window.location.pathname + window.location.search
        );

        window.open(response.data.authUrl, "_blank");

        toast.info("Please complete Google login in the new tab");
      } else if (response.data.message === "Already authenticated") {
        setIsAuthenticated(true);
        setShowLoginPrompt(false);
        getAllImageNames();
      }
      setLoading(false);
    } catch (error) {
      console.error("Login failed:", error);
      toast.error("Failed to login with Google");
      setLoading(false);
    }
  }, [getAllImageNames, setIsAuthenticated, setShowLoginPrompt, setLoading]);

  useEffect(() => {
    const returnPath = localStorage.getItem("imageListReturnPath");
    if (returnPath) {
      localStorage.removeItem("imageListReturnPath");
      checkAuthentication().then((isAuth) => {
        if (isAuth) {
          getAllImageNames();
        }
      });
    }
  }, [checkAuthentication, getAllImageNames]);

  const handleOpen = async () => {
    const isAuth = await checkAuthentication();

    if (isAuth) {
      setOpen(true);
      getAllImageNames();
    } else {
      setShowLoginPrompt(true);
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setIsImagesSelected(false);
    setImageList([]);
    setApiError(false);
    setShowLoginPrompt(false);
  };

  const getImage = async (imageName: string) => {
    setLoading(true);
    setIsImagesSelected(true);
    if (imageName) {
      try {
        const response = await googleDriveAxios.get(
          `/get_image_link_by_filename/${imageName}`
        );
        console.log(response.data.image_link);
        const previewUrl = response.data.image_link.replace(
          "/view",
          "/preview"
        );
        setGoogleDriveUrl(previewUrl);
        setLoading(false);
      } catch (error: any) {
        if (error.response && error.response.status === 401) {
          setIsAuthenticated(false);
          setShowLoginPrompt(true);
          setIsImagesSelected(false);
        } else {
          setApiError(true);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{
          color: imagesExist ? "white" : "#6A1B9A",
          fontSize: "48px",
          backgroundColor: imagesExist ? "#6A1B9A" : "white",
          "&:hover": {
            color: !imagesExist ? "white" : "#6A1B9A",
            backgroundColor: !imagesExist ? "#6A1B9A" : "white",
          },
        }}
        aria-label="Open image gallery"
      >
        <CollectionsIcon fontSize="small" />
      </IconButton>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="scan-dialog-title"
      >
        <Box sx={{ display: "flex", width: "100%", alignItems: "center" }}>
          {isImageselected && !showLoginPrompt && (
            <IconButton
              onClick={() => {
                setIsImagesSelected(false);
              }}
              sx={{
                color: "#6A1B9A",
                fontSize: "48px",
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}

          <DialogTitle
            id="scan-dialog-title"
            sx={{
              flexGrow: 1,
              textAlign: "center",
              fontWeight: "bold",
              backgroundColor: "#6A1B9A",
              color: "white",
              margin: 0,
            }}
          >
            {showLoginPrompt ? "Google Login Required" : "Checkpoint Images"}
          </DialogTitle>
        </Box>

        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: showLoginPrompt ? "20px" : "0px",
            backgroundColor: "#f5f5f5",
          }}
        >
          {loading ? (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 10,
              }}
            >
              <CircularProgress />
            </Box>
          ) : showLoginPrompt ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Google Drive access is required to view images
              </Typography>
              <Button
                variant="contained"
                onClick={handleGoogleLogin}
                startIcon={<LoginIcon />}
                sx={{
                  backgroundColor: "#6A1B9A",
                  "&:hover": { backgroundColor: "#4a1266" },
                }}
              >
                Login with Google
              </Button>
            </Box>
          ) : isImageselected ? (
            <Box
              sx={{
                position: "relative",
                width: "100%",
                overflow: "hidden",
              }}
            >
              <iframe
                src={googleDriveUrl}
                width="100%"
                height="500px"
                title="Google Drive file preview"
              ></iframe>
            </Box>
          ) : (
            <TableContainer
              sx={{
                filter: loading ? "blur(5px)" : "none",
                pointerEvents: loading ? "none" : "auto",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow className="table__head_row">
                    <TableCell
                      className="table__head_cell"
                      sx={{
                        textAlign: "center",
                        verticalAlign: "middle",
                        width: "100%",
                        padding: "0px",
                        overflowY: "auto",
                        border: "none",
                      }}
                    >
                      {checkPointName.toUpperCase()}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Box
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            zIndex: 10,
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : apiError ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography color="error">Invalid DC Number</Typography>
                      </TableCell>
                    </TableRow>
                  ) : imageList && imageList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography color="error">No Image Found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    imageList?.map((image, index) => {
                      return (
                        <TableRow hover key={index}>
                          <TableCell
                            onClick={() => getImage(image)}
                            sx={{
                              width: "100%",
                              textAlign: "center",
                              verticalAlign: "middle",
                              cursor: "pointer",
                            }}
                          >
                            {image}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImageList;
